import { CreditLimitExceededDetails, type InquiryKind } from "@ai-platform/contracts";
import { refusedFields } from "@ai-platform/ui/inquiry.ts";
import { type Audience, creditLimitExceededText, reasonText } from "../lib/reason-text.ts";
import { ApiRequestError, InvalidInputError } from "./client.ts";

/** The text of a failed change and whether a second try could help (from `failureOf`). */
export type Failure = { text: string; final: boolean };

export const RETRY_TEXT = "Das hat nicht geklappt. Bitte versucht es gleich noch einmal.";

/** The German sentence before the API's message of a `400 validation_failed` (N15). */
export const VALIDATION_FAILED_TEXT = "Diese Angaben sind so nicht möglich.";

/** A body over the API's limit of 64 KB (`413 payload_too_large`, RN15). */
export const PAYLOAD_TOO_LARGE_TEXT = "Diese Eingaben sind zu umfangreich. Bitte kürzt sie.";

/**
 * What to say about a change that failed, and whether a second try could help (`final: false`).
 * A refusal names its reason (K-04) and is final, except `credit_limit_exceeded`: a lower amount
 * may fit, and the text names the amounts from `details` (UC-10). A `400 validation_failed`
 * (the input is impossible as sent, e.g. the product terms of UC-26, ADR-125) is final, since
 * the form has checked the contract already; it says so in German and adds the API's message,
 * which is English, as the detail (N15). An input the portal itself found outside the contract
 * before sending (`InvalidInputError`) is the same case without an answer: final, with the same
 * sentence and no detail (GP2). A body over the API's limit (`413 payload_too_large`) is
 * final with its own text (RN15). A record that is gone (404) says `gone`. Anything else is worth
 * another try.
 *
 * @param error - what the mutation caught, usually an `ApiRequestError` of the client
 * @param gone - what to say instead when the record is no longer there
 * @param audience - who reads the text; it is required: the staff area passes `staff`, so a
 *   refusal speaks about the customer instead of to them, the customer area `customer`
 *   (`reasonText`); a caller without it fails the typecheck
 * @returns the text to show and whether a second try would only repeat the answer
 */
export function failureOf(error: unknown, gone: string, audience: Audience): Failure {
  // the same input again breaks the same contract: only another input helps
  if (error instanceof InvalidInputError) return { text: VALIDATION_FAILED_TEXT, final: true };
  if (!(error instanceof ApiRequestError)) return { text: RETRY_TEXT, final: false };
  if (error.code === "validation_failed") {
    return { text: `${VALIDATION_FAILED_TEXT} Meldung der Plattform: ${error.message}`, final: true };
  }
  // the same body again is just as long: only shorter input helps
  if (error.code === "payload_too_large") return { text: PAYLOAD_TOO_LARGE_TEXT, final: true };
  // the login's allowlist applies to the instance the team links, too (ADR-126); the host is
  // impossible as sent, a second try only repeats the answer
  if (error.code === "instance_mismatch") {
    return {
      text: "Diese Instanz gehört nicht zu den Instanzen, über die die Plattform Anmeldungen erlaubt.",
      final: true,
    };
  }
  if (error.reason === "credit_limit_exceeded") {
    const details = CreditLimitExceededDetails.safeParse(error.details);
    return {
      text: details.success ? creditLimitExceededText(details.data, audience) : reasonText(error.reason, audience),
      final: false,
    };
  }
  if (error.reason) return { text: reasonText(error.reason, audience), final: true };
  if (error.status === 404) return { text: gone, final: true };
  return { text: RETRY_TEXT, final: false };
}

export const INQUIRY_RATE_LIMITED_TEXT = "Von hier kamen gerade viele Anfragen. Bitte versucht es später noch einmal.";

export const INQUIRY_TOO_FAST_TEXT =
  "Das ging schneller, als man eine Anfrage schreibt. Bitte wartet einen Moment und sendet sie dann noch einmal.";

export const INQUIRY_PRODUCT_GONE_TEXT =
  "Dieses Produkt wird gerade nicht angeboten. Ladet die Seite neu und versucht es noch einmal.";

/**
 * The fields a `400 validation_failed` refused (`details[].path`, 07 §1 K-04), each once, for a
 * form to mark them, e.g. the price the terms of a product refuse for their sale (ADR-147); any
 * other failure names none.
 */
export function refusedFieldsOf(error: unknown): string[] {
  return error instanceof ApiRequestError && error.code === "validation_failed" ? refusedFields(error.details) : [];
}

/** Refusals `inquiryFailureText` says in its own words: the minimum time, a product no longer offered. */
const EXPLAINED_FIELDS: ReadonlySet<string> = new Set(["startedAt", "productId"]);

/**
 * The fields of an inquiry the API refused, for the form to mark those it shows; without the ones
 * the failure's text already explains, which no correction of the field would cure.
 */
export function inquiryRefusedFields(error: unknown): string[] {
  return refusedFieldsOf(error).filter((field) => !EXPLAINED_FIELDS.has(field));
}

/** The kinds sent without a session, the only ones with a start time (ADR-050). */
const TIMED_KINDS: ReadonlySet<InquiryKind> = new Set(["access", "application"]);

/**
 * What the inquiry form says when sending failed (UC-30, ADR-093): the rate limit of 07 §2.2; the
 * minimum time only for an `access` inquiry or an application whose 400 names `startedAt`
 * (ADR-050), since customers send no start time; a product the catalogue no longer shows when the
 * 400 names `productId`; anything else as `failureOf` says it, a refusal by its reason, a failure
 * as a second try (M16). A 400 at a field the form shows is marked there (`inquiryRefusedFields`).
 * Guests and customers write, staff do not (ADR-093), so the texts are the customer's.
 *
 * @param error - what the send caught, usually an `ApiRequestError` of the client
 * @param kind - the kind of the inquiry that was sent
 */
export function inquiryFailureText(error: unknown, kind: InquiryKind): string {
  if (error instanceof ApiRequestError && error.status === 429) return INQUIRY_RATE_LIMITED_TEXT;
  const refused = refusedFieldsOf(error);
  if (TIMED_KINDS.has(kind) && refused.includes("startedAt")) return INQUIRY_TOO_FAST_TEXT;
  if (refused.includes("productId")) return INQUIRY_PRODUCT_GONE_TEXT;
  return failureOf(error, RETRY_TEXT, "customer").text;
}
